import React, { useState, useEffect } from 'react'
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonChip,
  IonBadge,
  IonAlert,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonText,

} from '@ionic/react'
import {
  peopleOutline,
  addOutline,
  closeOutline,
  linkOutline,
  checkmarkCircleOutline,
  timeOutline,
  trashOutline,
  mailOutline,
  searchOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playBackOutline,
  playForwardOutline,
  arrowBackOutline,
  ellipsisVerticalOutline,
  personOutline,
  keyOutline,
  informationCircleOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { supabase } from '../services/supabase'
import InvitationManagement from './InvitationManagement'
import GroupManagement from './GroupManagement'
import LanguageSwitcher from './LanguageSwitcher'
import Avatar from './Avatar'

interface Coachee {
  id: string
  relationshipId: string
  email: string
  full_name: string
  avatar_url?: string
  profile_picture_url?: string
  profile_picture_visibility?: string
  status: 'active' | 'inactive' | 'pending'
  started_at: string
  last_session?: string
  last_interaction?: string
}

interface InvitationCode {
  id: string
  coach_id: string
  code: string
  created_at: string
  expires_at: string
  used: boolean
  used_by?: string
  used_at?: string
  max_uses: number
  current_uses: number
}

interface CoachManagementProps {
  isModalOpen?: boolean
  onModalOpenChange?: (isOpen: boolean) => void
}

/**
 * Coach Management Component
 * 
 * This component manages coach-coachee relationships through an invitation-based system:
 * 1. Coach generates invitation codes
 * 2. Coachee accepts invitation during registration or in profile settings
 * 3. System creates relationship in coach_coachee_relationships table
 * 4. Only invitation-based relationships are displayed here
 * 
 * Previous approach using coach_id in profiles table has been removed
 * to ensure all relationships go through proper invitation flow.
 */
const CoachManagement: React.FC<CoachManagementProps> = ({ 
  isModalOpen: externalModalOpen, 
  onModalOpenChange 
}) => {
  const { user } = useAuthStore()
  const { t, i18n } = useTranslation()
  
  const [coachees, setCoachees] = useState<Coachee[]>([])
  const [invitationCodes, setInvitationCodes] = useState<InvitationCode[]>([])
  const [internalModalOpen, setInternalModalOpen] = useState(false)
  
  // Use external modal state if provided, otherwise use internal state
  const isManageModalOpen = externalModalOpen !== undefined ? externalModalOpen : internalModalOpen
  const setIsManageModalOpen = (open: boolean) => {
    if (onModalOpenChange) {
      onModalOpenChange(open)
    } else {
      setInternalModalOpen(open)
    }
  }
  const [isGeneratingCode, setIsGeneratingCode] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [showRemoveCoacheeAlert, setShowRemoveCoacheeAlert] = useState(false)
  const [coacheeToRemove, setCoacheeToRemove] = useState<string | null>(null)
  const [selectedTab, setSelectedTab] = useState<'coachees' | 'invitations' | 'groups'>('coachees')
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      fetchCoachees()
      fetchInvitationCodes()
    }
  }, [user])

  const fetchCoachees = async () => {
    try {
      // Get relationships from coach_coachee_relationships table (invitation-based only)
      const { data: relationshipsData, error: relError } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          id,
          coachee_id,
          status,
          started_at,
          updated_at,
          profiles!coach_coachee_relationships_coachee_id_fkey (
            id,
            email,
            full_name,
            avatar_url,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .eq('coach_id', user!.id)
        .order('updated_at', { ascending: false })

      if (relError) {
        console.error('Error fetching coach-coachee relationships:', relError)
        throw relError
      }

      // Transform relationships data (only invitation-based relationships)
      const coacheesData: Coachee[] = relationshipsData
        ?.filter((rel: any) => rel.profiles) // Only include relationships with valid profiles
        .map((rel: any) => ({
          id: rel.profiles.id,
          relationshipId: rel.id,
          email: rel.profiles.email,
          full_name: rel.profiles.full_name || '',
          avatar_url: rel.profiles.avatar_url,
          profile_picture_url: rel.profiles.profile_picture_url,
          profile_picture_visibility: rel.profiles.profile_picture_visibility,
          status: rel.status,
          started_at: rel.started_at,
          last_interaction: rel.updated_at
        })) || []

      // Sort by last interaction (most recent first)
      coacheesData.sort((a, b) => {
        const dateA = new Date(a.last_interaction || a.started_at)
        const dateB = new Date(b.last_interaction || b.started_at)
        return dateB.getTime() - dateA.getTime()
      })

      setCoachees(coacheesData)
    } catch (error) {
      console.error('Error fetching coachees:', error)
    }
  }

  const fetchInvitationCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('invitation_codes')
        .select('*')
        .eq('coach_id', user!.id)
        .eq('status', 'pending') // Only fetch pending/active invitation codes
        .order('created_at', { ascending: false })

      if (error) throw error

      // Update status for expired invitations
      const now = new Date()
      const expiredInvitations = data?.filter(invitation => 
        new Date(invitation.expires_at) < now
      ) || []

      // Update expired invitations in the database
      if (expiredInvitations.length > 0) {
        const { error: updateError } = await supabase
          .from('invitation_codes')
          .update({ status: 'expired' })
          .in('id', expiredInvitations.map(inv => inv.id))

        if (updateError) {
          console.error('Error updating expired invitations:', updateError)
        }
      }

      // Filter out expired invitations from display
      const activeInvitations = data?.filter(invitation => 
        new Date(invitation.expires_at) >= now
      ) || []

      setInvitationCodes(activeInvitations)
    } catch (error) {
      console.error('Error fetching invitation codes:', error)
    }
  }

  const generateInvitationCode = async () => {
    setIsGeneratingCode(true)
    try {
      // Generate a unique code
      const code = `COACH-${new Date().getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`
      
      // Save to database
      const { data, error } = await supabase
        .from('invitation_codes')
        .insert([{
          coach_id: user!.id,
          code,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          max_uses: 1, // Single-use invitation codes for security
          current_uses: 0,
          status: 'pending'
        }])
        .select()
        .single()

      if (error) throw error

      setInvitationCodes(prev => [data, ...prev])
      setToastMessage(`Invitation code generated: ${code}`)
      setShowToast(true)
    } catch (error) {
      console.error('Error generating invitation code:', error)
      setAlertMessage('Failed to generate invitation code')
      setShowAlert(true)
    } finally {
      setIsGeneratingCode(false)
    }
  }

  // Removed searchCoachees function - coaches should only add coachees via invitation codes
  // This prevents privacy violations by not allowing coaches to see all registered coachees

  // Removed addCoachee function - coachees are now added only through invitation codes during registration

  const updateCoacheeStatus = async (coachee: Coachee, newStatus: 'active' | 'inactive' | 'pending') => {
    setIsUpdatingStatus(coachee.relationshipId)
    try {
      const { error } = await supabase
        .from('coach_coachee_relationships')
        .update({ status: newStatus })
        .eq('id', coachee.relationshipId)

      if (error) throw error

      setToastMessage(t('dashboard.coach.statusUpdated', 'Status updated successfully'))
      setShowToast(true)
      fetchCoachees()
    } catch (error) {
      console.error('Error updating coachee status:', error)
      setAlertMessage(t('dashboard.coach.statusUpdateError', 'Failed to update status'))
      setShowAlert(true)
    } finally {
      setIsUpdatingStatus(null)
    }
  }

  const removeCoachee = async () => {
    if (!coacheeToRemove) return

    try {
      const { error } = await supabase
        .from('coach_coachee_relationships')
        .delete()
        .eq('coach_id', user!.id)
        .eq('coachee_id', coacheeToRemove)

      if (error) throw error

      setToastMessage('Coachee relationship removed successfully')
      setShowToast(true)
      setShowRemoveCoacheeAlert(false)
      setCoacheeToRemove(null)
      fetchCoachees()
    } catch (error) {
      console.error('Error removing coachee:', error)
      setAlertMessage('Failed to remove coachee relationship')
      setShowAlert(true)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setToastMessage('Code copied to clipboard!')
      setShowToast(true)
    })
  }

  // Filter coachees based on search text and status
  const filteredCoachees = coachees.filter((coachee) => {
    // Status filter
    if (statusFilter !== 'all' && coachee.status !== statusFilter) {
      return false
    }
    
    // Search filter
    if (!searchText.trim()) return true
    
    const searchLower = searchText.toLowerCase()
    return (
      coachee.full_name?.toLowerCase().includes(searchLower) ||
      coachee.email.toLowerCase().includes(searchLower) ||
      coachee.status.toLowerCase().includes(searchLower)
    )
  })

  // Calculate pagination
  const totalPages = Math.ceil(filteredCoachees.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCoachees = filteredCoachees.slice(startIndex, endIndex)

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchText, statusFilter])

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
            <div className="flex items-center">
              <IonIcon icon={peopleOutline} className="mr-2 flex-shrink-0" />
              {t('dashboard.coach.recentCoachees', 'Recent Coachees')}
            </div>
            <IonButton 
              fill="clear" 
              size="small"
              onClick={() => setIsManageModalOpen(true)}
            >
              <IonIcon icon={addOutline} slot="start" className="hidden sm:block" />
              <IonIcon icon={ellipsisVerticalOutline} className="sm:hidden" />
              <span className="hidden sm:inline ml-2">Manage</span>
            </IonButton>
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {coachees.length > 0 ? (
            <IonList>
              {coachees.slice(0, 5).map((coachee) => (
                                          <IonItem key={coachee.id}>
                            <div className="flex items-center w-full py-2">
                              {/* Profile Picture */}
                              <div className="flex-shrink-0 mr-3">
                                <Avatar
                                  user={{
                                    id: coachee.id,
                                    full_name: coachee.full_name,
                                    email: coachee.email,
                                    profile_picture_url: coachee.avatar_url,
                                    profile_picture_visibility: 'public' // Assume public for coach-coachee relationships
                                  }}
                                  size="medium"
                                  className="w-10 h-10 sm:w-12 sm:h-12"
                                />
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="font-medium text-sm sm:text-base break-words leading-tight flex-1 min-w-0 mr-2">
                                    {coachee.full_name || coachee.email}
                                  </h3>
                                  <IonChip 
                                    className="flex-shrink-0 text-xs"
                                    color={coachee.status === 'active' ? 'success' : 
                                           coachee.status === 'pending' ? 'warning' : 'medium'}
                                  >
                                    {coachee.status === 'active' ? t('dashboard.coach.statusActive', 'Active') :
                                     coachee.status === 'pending' ? t('dashboard.coach.statusPending', 'Pending') :
                                     t('dashboard.coach.statusInactive', 'Inactive')}
                                  </IonChip>
                                </div>
                                <p className="text-xs sm:text-sm text-gray-600 break-words">{coachee.email}</p>
                              </div>
                            </div>
                          </IonItem>
              ))}
            </IonList>
          ) : (
            <div className="text-center py-8">
              <IonIcon icon={peopleOutline} className="text-4xl text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">{t('dashboard.coach.noCoacheesYet', 'No coachees yet')}</p>
              <IonButton 
                fill="outline" 
                size="small"
                onClick={() => setIsManageModalOpen(true)}
              >
                {t('dashboard.coach.addClient', 'Add Your First Coachee')}
              </IonButton>
            </div>
          )}
        </IonCardContent>
      </IonCard>

      {/* Manage Coachees Modal */}
      <IonModal isOpen={isManageModalOpen} onDidDismiss={() => setIsManageModalOpen(false)}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setIsManageModalOpen(false)}>
                <IonIcon icon={arrowBackOutline} />
              </IonButton>
            </IonButtons>
            <IonTitle>{t('dashboard.coach.manageCoachees', 'Manage Coachees')}</IonTitle>
            <IonButtons slot="end">
              <LanguageSwitcher />
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {/* Tab Segment */}
          <div className="px-4 mt-4">
            <IonSegment 
              value={selectedTab} 
              onIonChange={(e) => setSelectedTab(e.detail.value as 'coachees' | 'invitations' | 'groups')}
              style={{'--indicator-color': 'var(--ion-color-primary)'}}
            >
            <IonSegmentButton value="coachees" style={{'--padding-start': '4px', '--padding-end': '4px'}}>
              <IonIcon icon={peopleOutline} />
              <IonLabel>{t('dashboard.coach.coachees', 'Coachees')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="invitations" style={{'--padding-start': '4px', '--padding-end': '4px'}}>
              <IonIcon icon={mailOutline} />
              <IonLabel>{t('dashboard.coach.invitations', 'Invitations')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="groups" style={{'--padding-start': '4px', '--padding-end': '4px'}}>
              <IonIcon icon={peopleOutline} />
              <IonLabel>{t('dashboard.coach.groups', 'Groups')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
          </div>

          <div className="px-2 sm:px-4 py-4">
            {selectedTab === 'coachees' && (
          <IonGrid>
            <IonRow>
              <IonCol size="12" sizeMd="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={linkOutline} className="mr-2 flex-shrink-0" />
{t('dashboard.coach.invitationCodes', 'Invitation Codes')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('dashboard.coach.generateInvitationDescription', 'Generate invitation codes for new coachees to join your coaching program.')}
                    </p>
                    
                    <IonButton 
                      expand="block" 
                      onClick={generateInvitationCode}
                      disabled={isGeneratingCode}
                      className="mb-4"
                    >
                      <IonIcon icon={addOutline} slot="start" />
{isGeneratingCode ? t('dashboard.coach.generating', 'Generating...') : t('dashboard.coach.generateNewCode', 'Generate New Code')}
                    </IonButton>

                    {invitationCodes.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium">{t('dashboard.coach.activeCodes', 'Active Codes:')}</h4>
                        {invitationCodes.map((code) => (
                          <div key={code.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <code className="text-sm font-mono">{code.code}</code>
                              <IonButton 
                                fill="clear" 
                                size="small"
                                onClick={() => copyToClipboard(code.code)}
                              >
{t('dashboard.coach.copy', 'Copy')}
                              </IonButton>
                            </div>
                            <div className="flex items-center mt-2 text-xs text-gray-500">
                              <IonIcon icon={timeOutline} className="mr-1" />
{t('dashboard.coach.expires', 'Expires')}: {new Date(code.expires_at).toLocaleDateString()}
                              {code.used && (
                                <IonBadge color="success" className="ml-2">{t('dashboard.coach.used', 'Used')}</IonBadge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>

              <IonCol size="12" sizeMd="6">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={informationCircleOutline} className="mr-2 flex-shrink-0" />
{t('dashboard.coach.howItWorks', 'How It Works')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p><strong>1.</strong> {t('dashboard.coach.step1', 'Generate an invitation code above')}</p>
                      <p><strong>2.</strong> {t('dashboard.coach.step2', 'Share the code with your coachee')}</p>
                      <p><strong>3.</strong> {t('dashboard.coach.step3', "They'll enter it during registration")}</p>
                      <p><strong>4.</strong> {t('dashboard.coach.step4', "They'll automatically be assigned to you")}</p>
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-800">
                        <strong>{t('dashboard.coach.privacyNote', 'Privacy Note:')}</strong> {t('dashboard.coach.privacyDescription', 'This invitation-only system ensures coachee privacy and prevents unauthorized access to user information.')}
                      </p>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>

            {/* Current Coachees List */}
            <IonRow>
              <IonCol size="12">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                      <div className="flex items-center">
                        <IonIcon icon={peopleOutline} className="mr-2 flex-shrink-0" />
                        <span>{t('dashboard.coach.currentCoachees', 'Current Coachees')}</span>
                      </div>
                      {coachees.length > 0 && (
                        <IonBadge color="primary" className="hidden sm:flex">{filteredCoachees.length} {t('dashboard.coach.showing', 'showing')}</IonBadge>
                      )}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {coachees.length > 0 && (
                      <>
                        <div className="mb-4 relative">
                          <div className="sm:ml-7 sm:mr-12">
                            <IonSearchbar
                              value={searchText}
                              onIonInput={(e) => setSearchText(e.detail.value!)}
                              placeholder={t('dashboard.coach.searchCoachees', 'Search coachees by name, email, or status...')}
                              showClearButton="focus"
                              debounce={300}
                            />
                          </div>
                        </div>
                        
                        {/* Status Filter */}
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="flex items-center space-x-2">
                            <IonText className="text-sm text-gray-600">
                              {t('dashboard.coach.filterByStatus', 'Filter by status:')}
                            </IonText>
                            <IonSelect
                              value={statusFilter}
                              onIonChange={(e) => setStatusFilter(e.detail.value)}
                              interface="popover"
                              className="w-32 text-sm"
                            >
                              <IonSelectOption value="all">
                                {t('dashboard.coach.allStatuses', 'All')}
                              </IonSelectOption>
                              <IonSelectOption value="active">
                                {t('dashboard.coach.statusActive', 'Active')}
                              </IonSelectOption>
                              <IonSelectOption value="inactive">
                                {t('dashboard.coach.statusInactive', 'Inactive')}
                              </IonSelectOption>
                              <IonSelectOption value="pending">
                                {t('dashboard.coach.statusPending', 'Pending')}
                              </IonSelectOption>
                            </IonSelect>
                          </div>
                          
                          {(searchText.trim() || statusFilter !== 'all') && (
                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={() => {
                                setSearchText('')
                                setStatusFilter('all')
                              }}
                            >
                              {t('dashboard.coach.clearFilters', 'Clear Filters')}
                            </IonButton>
                          )}
                        </div>
                        

                      </>
                    )}
                    {coachees.length > 0 ? (
                      filteredCoachees.length > 0 ? (
                        <>
                          <IonList>
                            {paginatedCoachees.map((coachee) => (
                          <IonItem key={coachee.id}>
                            <div className="flex items-center w-full py-2">
                              {/* Profile Picture */}
                              <div className="flex-shrink-0 mr-3">
                                <Avatar
                                  user={{
                                    id: coachee.id,
                                    full_name: coachee.full_name,
                                    email: coachee.email,
                                    profile_picture_url: coachee.avatar_url,
                                    profile_picture_visibility: 'public' // Assume public for coach-coachee relationships
                                  }}
                                  size="medium"
                                  className="w-10 h-10 sm:w-12 sm:h-12"
                                />
                              </div>
                              
                              {/* Content - Desktop layout */}
                              <div className="hidden sm:flex flex-1 items-center justify-between">
                                <div className="flex-1 min-w-0 mr-4">
                                  <h3 className="font-medium text-base break-words leading-tight">{coachee.full_name || coachee.email}</h3>
                                  <p className="text-sm text-gray-600 break-words">{coachee.email}</p>
                                  <p className="text-xs text-gray-500 break-words">
                                    {t('dashboard.coach.started', 'Started')}: {new Date(coachee.started_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2 flex-shrink-0">
                                  <IonSelect
                                    value={coachee.status}
                                    onIonChange={(e) => updateCoacheeStatus(coachee, e.detail.value)}
                                    interface="popover"
                                    disabled={isUpdatingStatus === coachee.relationshipId}
                                    className={`status-select text-xs ${coachee.status === 'active' ? 'text-green-600' : 
                                               coachee.status === 'pending' ? 'text-yellow-600' : 'text-gray-600'}`}
                                  >
                                    <IonSelectOption value="active">
                                      {t('dashboard.coach.statusActive', 'Active')}
                                    </IonSelectOption>
                                    <IonSelectOption value="inactive">
                                      {t('dashboard.coach.statusInactive', 'Inactive')}
                                    </IonSelectOption>
                                    <IonSelectOption value="pending">
                                      {t('dashboard.coach.statusPending', 'Pending')}
                                    </IonSelectOption>
                                  </IonSelect>
                                  <IonButton 
                                    fill="clear" 
                                    color="danger" 
                                    size="small"
                                    onClick={() => {
                                      setCoacheeToRemove(coachee.id)
                                      setShowRemoveCoacheeAlert(true)
                                    }}
                                  >
                                    <IonIcon icon={trashOutline} />
                                  </IonButton>
                                </div>
                              </div>
                              
                              {/* Content - Mobile layout */}
                              <div className="sm:hidden flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="font-medium text-sm break-words leading-tight flex-1 min-w-0 mr-2">
                                    {coachee.full_name || coachee.email}
                                  </h3>
                                  <IonChip 
                                    className="flex-shrink-0 text-xs"
                                    color={coachee.status === 'active' ? 'success' : 
                                           coachee.status === 'pending' ? 'warning' : 'medium'}
                                  >
                                    {coachee.status === 'active' ? t('dashboard.coach.statusActive', 'Active') :
                                     coachee.status === 'pending' ? t('dashboard.coach.statusPending', 'Pending') :
                                     t('dashboard.coach.statusInactive', 'Inactive')}
                                  </IonChip>
                                </div>
                                <p className="text-xs text-gray-600 break-words">{coachee.email}</p>
                                <p className="text-xs text-gray-500 break-words">
                                  {t('dashboard.coach.started', 'Started')}: {new Date(coachee.started_at).toLocaleDateString()}
                                </p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <IonSelect
                                    value={coachee.status}
                                    onIonChange={(e) => updateCoacheeStatus(coachee, e.detail.value)}
                                    interface="popover"
                                    disabled={isUpdatingStatus === coachee.relationshipId}
                                    className="text-xs flex-1"
                                  >
                                    <IonSelectOption value="active">
                                      {t('dashboard.coach.statusActive', 'Active')}
                                    </IonSelectOption>
                                    <IonSelectOption value="inactive">
                                      {t('dashboard.coach.statusInactive', 'Inactive')}
                                    </IonSelectOption>
                                    <IonSelectOption value="pending">
                                      {t('dashboard.coach.statusPending', 'Pending')}
                                    </IonSelectOption>
                                  </IonSelect>
                                  <IonButton 
                                    fill="clear" 
                                    color="danger" 
                                    size="small"
                                    onClick={() => {
                                      setCoacheeToRemove(coachee.id)
                                      setShowRemoveCoacheeAlert(true)
                                    }}
                                  >
                                    <IonIcon icon={trashOutline} />
                                  </IonButton>
                                </div>
                              </div>
                            </div>
                          </IonItem>
                        ))}
                          </IonList>
                          
                          {/* Pagination Controls */}
                          <div className="flex flex-col space-y-4 mt-4">
                            {/* Items per page selector and results info */}
                            <div className="flex flex-nowrap items-center justify-between text-sm">
                              <div className="flex items-center space-x-1">
                                <span className="text-gray-600">{t('common.show', 'Show')}:</span>
                                <IonSelect
                                  value={itemsPerPage}
                                  onIonChange={(e) => handlePageSizeChange(e.detail.value)}
                                  interface="popover"
                                  className="w-16"
                                >
                                  <IonSelectOption value={3}>3</IonSelectOption>
                                  <IonSelectOption value={5}>5</IonSelectOption>
                                  <IonSelectOption value={10}>10</IonSelectOption>
                                  <IonSelectOption value={25}>25</IonSelectOption>
                                  <IonSelectOption value={50}>50</IonSelectOption>
                                </IonSelect>
                              </div>
                              <div className="text-gray-600">
                                {t('common.showing', 'Showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCoachees.length)} {t('common.of', 'of')} {filteredCoachees.length}
                              </div>
                            </div>

                            {/* Navigation controls */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between">
                                {/* Desktop navigation - full text */}
                                <div className="hidden sm:flex items-center space-x-2">
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(1)}
                                    className="min-w-[60px]"
                                  >
                                    <IonIcon icon={playBackOutline} slot="start" />
                                    {t('common.first', 'First')}
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    className="min-w-[80px]"
                                  >
                                    <IonIcon icon={chevronBackOutline} slot="start" />
                                    {t('common.previous', 'Previous')}
                                  </IonButton>
                                </div>

                                {/* Mobile navigation - icons only */}
                                <div className="flex sm:hidden items-center space-x-1">
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(1)}
                                  >
                                    <IonIcon icon={playBackOutline} />
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                  >
                                    <IonIcon icon={chevronBackOutline} />
                                  </IonButton>
                                </div>

                                {/* Page indicator */}
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-600 hidden sm:block">
                                    {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
                                  </span>
                                  <span className="text-sm text-gray-600 sm:hidden">
                                    {currentPage}/{totalPages}
                                  </span>
                                </div>

                                {/* Desktop navigation - full text */}
                                <div className="hidden sm:flex items-center space-x-2">
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    className="min-w-[60px]"
                                  >
                                    {t('common.next', 'Next')}
                                    <IonIcon icon={chevronForwardOutline} slot="end" />
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(totalPages)}
                                    className="min-w-[60px]"
                                  >
                                    {t('common.last', 'Last')}
                                    <IonIcon icon={playForwardOutline} slot="end" />
                                  </IonButton>
                                </div>

                                {/* Mobile navigation - icons only */}
                                <div className="flex sm:hidden items-center space-x-1">
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                  >
                                    <IonIcon icon={chevronForwardOutline} />
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(totalPages)}
                                  >
                                    <IonIcon icon={playForwardOutline} />
                                  </IonButton>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
                          <p className="text-gray-500 mb-2">{t('dashboard.coach.noSearchResults', 'No coachees found')}</p>
                          <p className="text-sm text-gray-400">
                            {t('dashboard.coach.tryDifferentSearch', 'Try a different search term or clear the filter')}
                          </p>
                        </div>
                      )
                    ) : (
                      <p className="text-center text-gray-500 py-4">{t('dashboard.coach.noCoacheesYet', 'No coachees added yet')}</p>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>
            )}

            {selectedTab === 'invitations' && (
              <InvitationManagement />
            )}

            {selectedTab === 'groups' && (
              <GroupManagement />
            )}
          </div>
        </IonContent>
      </IonModal>



      {/* Toast for notifications */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="top"
        color="success"
      />

      {/* Alert for errors */}
      <IonAlert
        isOpen={showAlert}
        onDidDismiss={() => setShowAlert(false)}
        header="Error"
        message={alertMessage}
        buttons={['OK']}
      />

      {/* Remove Coachee Alert */}
      <IonAlert
        isOpen={showRemoveCoacheeAlert}
        onDidDismiss={() => setShowRemoveCoacheeAlert(false)}
        header={t('dashboard.coach.removeCoachee', 'Remove Coachee')}
        message={t('dashboard.coach.removeCoacheeConfirm', 'Are you sure you want to remove this coaching relationship? The coachee will no longer have access to your coaching services.')}
        buttons={[
                      {
              text: t('common.cancel', 'Cancel'),
              role: 'cancel'
            },
            {
              text: t('dashboard.coach.remove', 'Remove'),
              handler: removeCoachee
            }
        ]}
      />


    </>
  )
}

export default CoachManagement 